/* @flow */
/**
 * Field: part of an application can be added in a flow code block.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 * @typedef { Object } Field
 * @memberof FieldService
 */
import { root } from './conf'
import http from 'axios'
import uuid from 'uuid'
import Group from './Group'
import _ from 'lodash'

/**
 * UploadedApp typename
 * @private
 * @memberof BatchService
 */
type UploadedApp = {
  application: Object,
  manufacturer: Object,
  fields: Array<Object>,
  flows: Array<Object>
}

/**
 * Takes an object representing an uploaded application, and creates it, and all its references through batch mechanism.
 * @function
 * @memberof BatchService
 *
 * @return { Promise<any> } The promise for the batch operation request.
 */
const createUploadedAppBatch = (app: UploadedApp, shouldGenerateIds: boolean): Promise<any> => {
  const appId = shouldGenerateIds ? uuid.v4() : app.application.id
  if (shouldGenerateIds) {
    app.application.id = appId
    app.fields.forEach(f => {
      f.id = uuid.v4()
      f.application = appId
    })
    app.flows.forEach(f => {
      f.id = uuid.v4()
      f.application = appId
    })
  }
  const batch = {
    application: [mapObjectToBatchObject(app.application)],
    manufacturer: [mapObjectToBatchObject(app.manufacturer)],
    field: app.fields.map(f => mapObjectToBatchObject(f)),
    flow: app.flows.map(f => mapObjectToBatchObject(f))
  }
  if (!app.manufacturer || !app.manufacturer.id || !app.manufacturer.name) {
    delete batch.manufacturer
  }

  return http.post(`${root}/providebatch`, { batch }).then(() => appId)
}

const groupsThatNoLongerReferenceEntity = (allGroups, entityType, entityId) => {
  const groupsToModify = _.filter(allGroups, g => _.some(g[entityType], id => id === entityId))
  groupsToModify.forEach(g => (g[entityType] = _.without(g[entityType], entityId)))
  return groupsToModify
}

const removeAllGroupsFromApp = (appId: string, flowIds: Array<string>) => {
  return Group.readAllGroups().then(allGroups => {
    const appGroupsToModify = groupsThatNoLongerReferenceEntity(allGroups, 'application', appId)
    const flowGroupsToModify = _.union.apply(
      [],
      flowIds.map(flowId => groupsThatNoLongerReferenceEntity(allGroups, 'flow', flowId))
    )
    // Some group objects occur in both lists
    return _.union(appGroupsToModify, flowGroupsToModify)
  })
}

const deleteAppBatch = (
  appId: string,
  flowIds: Array<string>,
  fieldIds: Array<string>
): Promise<any> => {
  // 1: Remove all group references to app and flows in a batch transaction
  return (
    removeAllGroupsFromApp(appId, flowIds)
      .then(modifiedGroups => {
        return modifiedGroups.length === 0
          ? Promise.resolve()
          : http.post(`${root}/providebatch`, {
              batch: { group: modifiedGroups.map(mapObjectToBatchObject) }
            })
      })
      // 2: Delete all app entities in a second batch transaction
      .then(() =>
        http.post(`${root}/withdrawbatch`, {
          batch: {
            application: [appId],
            flow: flowIds,
            field: fieldIds
          }
        })
      )
  )
}

/**
 * Maps given object to format required by batch mechanism.
 * @function
 * @memberof BatchService
 *
 * @return { Object } Object in the format required by batch mechanism.
 */
const mapObjectToBatchObject = (object: Object): Object => {
  let descriptive = {
    ...object
  }
  delete descriptive.id
  delete descriptive.name
  delete descriptive.version

  return {
    id: object.id,
    name: object.name,
    version: object.version,
    descriptive: descriptive
  }
}

const self = {
  createUploadedAppBatch,
  deleteAppBatch
}
/**
 * Batch service: Interface for carrying out batch operations.
 * @module BatchService
 * @namespace BatchService
 * @version 1.0.0
 */
export default self
